/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms/views/View'
import { mono, boFont } from './backoffice.tokens'

export interface BoSparkPoint {
	label: string
	value: number
}

interface PropsType {
	points: BoSparkPoint[]
	/** 왼쪽 위 키커 — `일별 주문` */
	caption?: string
	/** 오른쪽 위 요약. 기본 = 합계 */
	summary?: React.ReactNode
	height?: number
	format?: (n: number) => string
	className?: string
}

const W = 600

/**
 * 단일 계열 스파크라인 — 인라인 SVG, 라이브러리 없음. 색은 ink 한 가지(모노크롬, 계열이 하나라 범례 없음).
 * 값은 툴팁으로만 읽게 두지 않는다 — 점 위에 올리면 오른쪽 요약이 그 날 값으로 바뀌고, 합계는 늘 보인다.
 * 폭은 컨테이너에 맞춰 늘어나고 선 굵기는 `vector-effect` 로 고정한다.
 */
export const BoSparkline = (props: PropsType) => {
	const H = props.height ?? 56
	/** 면 채움 그라데이션 id — 한 화면에 스파크라인이 여럿이라 id 가 겹치면 안 된다 */
	const gid = React.useId().replace(/:/g, '')
	const format = props.format ?? ((n: number) => n.toLocaleString('en-US'))
	const [hover, setHover] = React.useState<number>(-1)

	const n = props.points.length
	const max = Math.max(1, ...props.points.map(p => p.value))
	const sum = props.points.reduce<number>((s, p) => s + p.value, 0)
	const pad = 3
	const x = (i: number) => (n <= 1 ? W / 2 : (i / (n - 1)) * W)
	const y = (v: number) => H - pad - (v / max) * (H - pad * 2)
	const line = props.points.map((p, i) => `${x(i)},${y(p.value)}`).join(' ')
	const area = n > 0 ? `0,${H} ${line} ${W},${H}` : ''
	const last = n > 0 ? props.points[n - 1] : null
	const hovered = hover >= 0 && hover < n ? props.points[hover] : null

	return (
		<View css={style} className={`boSparkline ${props.className ?? ''}`}>
			<View className="boSparkHead">
				{props.caption !== undefined ? (
					<span className="boSparkCaption">{props.caption}</span>
				) : (
					<span />
				)}
				<span className="boSparkSummary">
					{hovered !== null ? (
						<>
							<span className="boSparkLabel">{hovered.label}</span>{' '}
							{format(hovered.value)}
						</>
					) : props.summary !== undefined ? (
						props.summary
					) : (
						<>
							<span className="boSparkLabel">합계</span> {format(sum)}
						</>
					)}
				</span>
			</View>
			{n === 0 ? (
				<View className="boSparkEmpty" style={{ height: H }} />
			) : (
				<View className="boSparkPlot" style={{ height: H }}>
					<svg
						className="boSparkSvg"
						viewBox={`0 0 ${W} ${H}`}
						preserveAspectRatio="none"
						style={{ height: H }}
						role="img"
						aria-label={props.caption ?? 'sparkline'}
						onMouseLeave={() => setHover(-1)}
					>
						<defs>
							<linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
								<stop className="boSparkStopA" offset="0" />
								<stop className="boSparkStopB" offset="1" />
							</linearGradient>
						</defs>
						<polygon className="boSparkArea" points={area} fill={`url(#${gid})`} />
						<polyline
							className="boSparkPath"
							points={line}
							fill="none"
							strokeLinejoin="round"
							strokeLinecap="round"
							vectorEffect="non-scaling-stroke"
						/>
						{hovered !== null ? (
							<line
								className="boSparkCross"
								x1={x(hover)}
								x2={x(hover)}
								y1={0}
								y2={H}
								vectorEffect="non-scaling-stroke"
							/>
						) : null}
						{props.points.map((p, i) => (
							<rect
								key={p.label}
								x={n <= 1 ? 0 : x(i) - W / (n - 1) / 2}
								y={0}
								width={n <= 1 ? W : W / (n - 1)}
								height={H}
								fill="transparent"
								onMouseEnter={() => setHover(i)}
							>
								<title>{`${p.label} · ${format(p.value)}`}</title>
							</rect>
						))}
					</svg>
					{last !== null ? (
						<i className="boSparkDot" style={{ top: y(last.value) }} aria-hidden="true" />
					) : null}
				</View>
			)}
		</View>
	)
}

const style = css({
	display: 'flex',
	flexDirection: 'column',
	gap: 8,
	minWidth: 0,
	fontFamily: boFont.ui,

	'.boSparkHead': {
		display: 'flex',
		alignItems: 'baseline',
		justifyContent: 'space-between',
		gap: 12,
	},
	'.boSparkCaption': {
		fontSize: 11,
		letterSpacing: '.18em',
		textTransform: 'uppercase',
		fontWeight: 500,
		color: mono.faint,
	},
	'.boSparkSummary': {
		fontSize: 13,
		fontVariantNumeric: 'tabular-nums',
		color: mono.ink,
		whiteSpace: 'nowrap',
	},
	'.boSparkLabel': { color: mono.dim, marginRight: 4 },
	/** 색은 전부 여기서 정한다 — 쓰는 화면이 클래스로 덮을 수 있어야 한다(홈 다크) */
	'.boSparkPlot': { position: 'relative', width: '100%' },
	'.boSparkSvg': { display: 'block', width: '100%', overflow: 'visible' },
	'.boSparkStopA': { stopColor: mono.ink, stopOpacity: 0.12 },
	'.boSparkStopB': { stopColor: mono.ink, stopOpacity: 0 },
	'.boSparkPath': { stroke: mono.ink, strokeWidth: 2 },
	'.boSparkCross': { stroke: mono.lineSoft, strokeWidth: 1 },
	/** 끝점은 SVG 밖이다 — `preserveAspectRatio="none"` 안에서는 원이 타원으로 늘어난다 */
	'.boSparkDot': {
		position: 'absolute',
		right: 0,
		width: 8,
		height: 8,
		borderRadius: '50%',
		background: mono.ink,
		boxShadow: `0 0 0 2px ${mono.surface}`,
		transform: 'translate(50%, -50%)',
		pointerEvents: 'none',
	},
	'.boSparkEmpty': { borderBottom: `1px solid ${mono.line}` },
})
